import { useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { ArrowLeft, Mail, Phone, User, CheckCircle, Loader2, Lock, Eye, EyeOff, Check, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Logo URLs
const LOGO_LIGHT = "/logo-light.png";
const LOGO_DARK = "/logo-dark.png";

export default function Register() {
  const { t, language } = useLanguage();
  const { isDarkMode } = useTheme();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    emailCode: '',
    phone: '',
    phoneCode: '',
    name: '',
    password: '',
    confirmPassword: ''
  });
  const [nameError, setNameError] = useState('');
  const [emailVerified, setEmailVerified] = useState(false);
  const [phoneVerified, setPhoneVerified] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const navigate = useNavigate();

  const emailOtpRefs = useRef([]);
  const phoneOtpRefs = useRef([]);

  // Password validation
  const passwordRequirements = {
    minLength: formData.password.length >= 10,
    hasUppercase: /[A-Z]/.test(formData.password),
    hasLowercase: /[a-z]/.test(formData.password),
    hasNumber: /[0-9]/.test(formData.password),
    hasSymbol: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password)
  };

  const isPasswordValid = Object.values(passwordRequirements).every(Boolean);
  const doPasswordsMatch = formData.password === formData.confirmPassword && formData.confirmPassword.length > 0;

  // Validate English-only name
  const validateEnglishName = (name) => {
    if (!name || !name.trim()) return false;
    const englishOnly = /^[a-zA-Z\s'-]+$/;
    return englishOnly.test(name) && name.trim().length > 0;
  };

  const handleNameChange = (e) => {
    const value = e.target.value;
    setFormData({ ...formData, name: value });
    
    if (value && !validateEnglishName(value)) {
      setNameError(t('englishOnly'));
    } else {
      setNameError('');
    }
  };

  const handleOtpChange = (index, value, type) => {
    // Only accept digits
    if (value && !/^\d$/.test(value.slice(-1))) return;
    
    // Get the last character if multiple were typed
    const digit = value ? value.slice(-1) : '';
    
    if (type === 'email') {
      setFormData(prev => {
        const codeArray = prev.emailCode.padEnd(6, ' ').split('');
        codeArray[index] = digit;
        return { ...prev, emailCode: codeArray.join('').replace(/ /g, '') };
      });
      
      // Auto-focus next input
      if (digit && index < 5) {
        setTimeout(() => {
          emailOtpRefs.current[index + 1]?.focus();
        }, 10);
      }
    } else {
      setFormData(prev => {
        const codeArray = prev.phoneCode.padEnd(6, ' ').split('');
        codeArray[index] = digit;
        return { ...prev, phoneCode: codeArray.join('').replace(/ /g, '') };
      });
      
      // Auto-focus next input
      if (digit && index < 5) {
        setTimeout(() => {
          phoneOtpRefs.current[index + 1]?.focus();
        }, 10);
      }
    }
  };

  const handleOtpKeyDown = (index, e, type) => {
    const refs = type === 'email' ? emailOtpRefs : phoneOtpRefs;
    
    if (e.key === 'Backspace') {
      e.preventDefault();
      
      if (type === 'email') {
        setFormData(prev => {
          const codeArray = prev.emailCode.padEnd(6, ' ').split('');
          if (codeArray[index] && codeArray[index] !== ' ') {
            codeArray[index] = ' ';
          } else if (index > 0) {
            codeArray[index - 1] = ' ';
            setTimeout(() => refs.current[index - 1]?.focus(), 10);
          }
          return { ...prev, emailCode: codeArray.join('').replace(/ /g, '') };
        });
      } else {
        setFormData(prev => {
          const codeArray = prev.phoneCode.padEnd(6, ' ').split('');
          if (codeArray[index] && codeArray[index] !== ' ') {
            codeArray[index] = ' ';
          } else if (index > 0) {
            codeArray[index - 1] = ' ';
            setTimeout(() => refs.current[index - 1]?.focus(), 10);
          }
          return { ...prev, phoneCode: codeArray.join('').replace(/ /g, '') };
        });
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      refs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < 5) {
      refs.current[index + 1]?.focus();
    }
  };

  const handleOtpPaste = (e, type) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    if (type === 'email') {
      setFormData({ ...formData, emailCode: paste });
    } else {
      setFormData({ ...formData, phoneCode: paste });
    }
  };

  const sendEmailCode = async () => {
    if (!formData.email) {
      toast.error(t('pleaseEnterEmail'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/verification/send-email-code`, {
        email: formData.email
      });
      toast.success(t('codeSentEmail'));
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('failedSendCode'));
    } finally {
      setLoading(false);
    }
  };

  const verifyEmailCode = async () => {
    if (formData.emailCode.length !== 6) {
      toast.error(t('pleaseEnter6Digits'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/verification/verify-email-code`, {
        email: formData.email,
        code: formData.emailCode
      });
      setEmailVerified(true);
      toast.success(t('emailVerifiedSuccess'));
      setStep(3);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invalidCode'));
    } finally {
      setLoading(false);
    }
  };

  const sendPhoneCode = async () => {
    if (!formData.phone) {
      toast.error(t('pleaseEnterPhone'));
      return;
    }
    const fullPhone = `+971${formData.phone.replace(/^0/, '')}`;
    setLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/verification/send-phone-code`, {
        phone: fullPhone
      });
      toast.success(t('codeSentPhone'));
      setStep(4);
    } catch (error) {
      toast.error(error.response?.data?.detail || t('failedSendCode'));
    } finally {
      setLoading(false);
    }
  };

  const verifyPhoneCode = async () => {
    if (formData.phoneCode.length !== 6) {
      toast.error(t('pleaseEnter6Digits'));
      return;
    }
    const fullPhone = `+971${formData.phone.replace(/^0/, '')}`;
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/verification/verify-phone-code`, {
        phone: fullPhone,
        code: formData.phoneCode
      });
      if (response.data.verified) {
        setPhoneVerified(true);
        toast.success(t('phoneVerifiedSuccess'));
        setStep(5);
      } else {
        toast.error(t('invalidCode'));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || t('invalidCode'));
    } finally {
      setLoading(false);
    }
  };

  const saveName = () => {
    if (!formData.name.trim()) {
      toast.error(t('pleaseEnterName'));
      return;
    }
    if (!validateEnglishName(formData.name)) {
      toast.error(t('englishOnly'));
      return;
    }
    setStep(6);
  };

  const completeRegistration = async () => {
    // Validate password
    if (!isPasswordValid) {
      toast.error(language === 'ar' ? 'كلمة المرور لا تستوفي المتطلبات' : 'Password does not meet requirements');
      return;
    }
    if (!doPasswordsMatch) {
      toast.error(language === 'ar' ? 'كلمات المرور غير متطابقة' : 'Passwords do not match');
      return;
    }

    const fullPhone = `+971${formData.phone.replace(/^0/, '')}`;
    setLoading(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/register-business`, {
        email: formData.email,
        phone: fullPhone,
        name: formData.name,
        password: formData.password
      });
      localStorage.setItem('business_token', response.data.access_token);
      localStorage.setItem('business_user', JSON.stringify(response.data.user));
      toast.success(t('accountCreated'));
      // Dispatch event and redirect immediately
      window.dispatchEvent(new Event('userLoggedIn'));
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || t('failedSendCode'));
      setLoading(false);
    }
  };

  const goBack = () => {
    if (step > 1) setStep(step - 1);
    else navigate('/');
  };

  const OtpInputs = ({ value, type }) => (
    <div className="flex justify-center gap-2 sm:gap-3" dir="ltr" onPaste={(e) => handleOtpPaste(e, type)}>
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <input
          key={index}
          ref={(el) => {
            if (type === 'email') emailOtpRefs.current[index] = el;
            else phoneOtpRefs.current[index] = el;
          }}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={value[index] || ''}
          onChange={(e) => handleOtpChange(index, e.target.value, type)}
          onKeyDown={(e) => handleOtpKeyDown(index, e, type)}
          onFocus={(e) => e.target.select()}
          autoComplete="one-time-code"
          className={`w-10 h-12 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-bold border-2 rounded-lg focus:border-[#38BDF8] focus:ring-2 focus:ring-[#38BDF8]/20 outline-none transition-all ${
            isDarkMode 
              ? 'bg-[#1E293B] border-[#334155] text-white' 
              : 'bg-white border-gray-200 text-gray-900'
          }`}
          data-testid={`${type}-otp-${index}`}
        />
      ))}
    </div>
  );

  return (
    <div className={`min-h-screen flex ${language === 'ar' ? 'flex-row-reverse' : 'flex-row'} cgm-page`}>
      {/* Background Effects */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className={`absolute top-20 start-10 w-[300px] h-[300px] rounded-full blur-[100px] ${isDarkMode ? 'bg-[#38BDF8]/10' : 'bg-[#38BDF8]/5'}`} />
        <div className={`absolute bottom-20 end-10 w-[250px] h-[250px] rounded-full blur-[80px] ${isDarkMode ? 'bg-[#F97316]/10' : 'bg-[#F97316]/5'}`} />
      </div>

      {/* Left Panel - App Download (Desktop only) */}
      <div className={`hidden lg:flex lg:w-1/2 flex-col items-center justify-center p-8 relative overflow-hidden ${
        isDarkMode ? 'bg-[#131B2C]/50' : 'bg-gradient-to-br from-[#38BDF8]/10 to-[#F97316]/10'
      }`}>
        <button
          onClick={goBack}
          className={`absolute top-6 ${language === 'ar' ? 'right-6' : 'left-6'} px-4 py-2 rounded-xl flex items-center gap-2 transition-colors z-10 ${
            isDarkMode 
              ? 'bg-white/10 text-white hover:bg-white/20' 
              : 'bg-gray-900 text-white hover:bg-gray-800'
          }`}
          data-testid="back-btn-desktop"
        >
          <ArrowLeft className="h-4 w-4 rtl:rotate-180" />
          {language === 'ar' ? 'رجوع' : 'Back'}
        </button>
        
        <div className={`relative rounded-2xl p-6 shadow-2xl mb-8 ${isDarkMode ? 'bg-[#1E293B]' : 'bg-white'}`}>
          <img 
            src="https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=https://cloudgm.business/app"
            alt="QR Code"
            className="w-48 h-48"
          />
        </div>
        
        <h2 className={`relative text-2xl font-bold text-center mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('downloadApp')}</h2>
        <p className={`relative text-center mb-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('manageOrders')}</p>
        
        <div className="relative flex flex-col gap-3">
          <img 
            src="https://customer-assets.emergentagent.com/job_cloudgm-hub-1/artifacts/u9oigb07_freepik__background__48618.png" 
            alt="Download Apps" 
            className="w-full max-w-[200px] mx-auto cursor-pointer hover:opacity-90 transition-opacity rounded-lg" 
          />
        </div>
      </div>

      {/* Right Panel - Registration Form */}
      <div className={`w-full lg:w-1/2 flex flex-col relative overflow-hidden ${isDarkMode ? 'bg-[#0B0F19]' : 'bg-white'}`}>
        {/* Mobile Header */}
        <div className="lg:hidden p-4 flex items-center relative z-10">
          <button
            onClick={goBack}
            className={`p-2 rounded-xl transition-colors ${isDarkMode ? 'hover:bg-white/10' : 'hover:bg-gray-100'}`}
            data-testid="back-btn-mobile"
          >
            <ArrowLeft className={`h-6 w-6 ${language === 'en' ? '' : 'rotate-180'} ${isDarkMode ? 'text-white' : 'text-gray-900'}`} />
          </button>
        </div>

        <div className="flex-1 flex items-center justify-center p-6 sm:p-8">
          <div className="w-full max-w-md">
            {/* Step 1: Enter Email */}
            {step === 1 && (
              <div className="space-y-6" data-testid="step-1">
                <div className="text-center">
                  <Link to="/" className="inline-block mb-6">
                    <img src={isDarkMode ? LOGO_DARK : LOGO_LIGHT} alt="CloudGM" className="h-24 sm:h-28 mx-auto" />
                  </Link>
                  <h1 className={`text-2xl sm:text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('getStarted')}</h1>
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{t('enterEmail')}</p>
                </div>

                <div className="space-y-4">
                  <div className="relative">
                    <Mail className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    <Input
                      type="email"
                      placeholder={t('enterEmailPlaceholder')}
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className={`${language === 'ar' ? 'pr-10' : 'pl-10'} py-6 text-base ${
                        isDarkMode 
                          ? 'bg-[#1E293B] border-[#334155] text-white placeholder:text-gray-500' 
                          : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                      }`}
                      dir="ltr"
                      data-testid="email-input"
                    />
                  </div>

                  <Button
                    onClick={sendEmailCode}
                    disabled={loading || !formData.email}
                    className="w-full bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] hover:opacity-90 disabled:opacity-50 text-white py-6 text-lg font-semibold"
                    data-testid="send-email-code-btn"
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('sendVerificationCode')}
                  </Button>
                </div>

                <div className="text-center pt-4">
                  <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>{t('lookingCustomer')}</p>
                  <a href="https://cloudgm.com" className="text-[#38BDF8] hover:underline font-medium">
                    {t('visitMarketplace')}
                  </a>
                </div>
              </div>
            )}

            {/* Step 2: Verify Email Code */}
            {step === 2 && (
              <div className="space-y-6" data-testid="step-2">
                <div className="text-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-[#38BDF8]/20' : 'bg-[#38BDF8]/10'}`}>
                    <Mail className="h-8 w-8 text-[#38BDF8]" />
                  </div>
                  <h1 className={`text-2xl sm:text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('verifyEmail')}</h1>
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{t('enterCode')}</p>
                  <p className="text-[#38BDF8] font-medium" dir="ltr">{formData.email}</p>
                </div>

                <OtpInputs value={formData.emailCode} type="email" />

                <Button
                  onClick={verifyEmailCode}
                  disabled={loading || formData.emailCode.length !== 6}
                  className="w-full bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] hover:opacity-90 text-white py-6 text-lg font-semibold"
                  data-testid="verify-email-btn"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('verify')}
                </Button>

                <button
                  onClick={sendEmailCode}
                  className="w-full text-[#38BDF8] hover:underline text-sm"
                  disabled={loading}
                >
                  {t('resendCode')}
                </button>
              </div>
            )}

            {/* Step 3: Enter Phone */}
            {step === 3 && (
              <div className="space-y-6" data-testid="step-3">
                <div className="text-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-green-500/20' : 'bg-green-100'}`}>
                    <CheckCircle className="h-8 w-8 text-green-500" />
                  </div>
                  <p className="text-green-500 font-medium mb-4">{t('emailVerified')}</p>
                  <h1 className={`text-2xl sm:text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('phoneNumber')}</h1>
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{t('enterPhone')}</p>
                </div>

                <div className="space-y-4">
                  <Label className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>{t('phoneNumber')}</Label>
                  <div className="flex gap-2" dir="ltr">
                    <div className={`flex items-center gap-2 px-3 py-3 rounded-lg border ${
                      isDarkMode ? 'bg-[#1E293B] border-[#334155]' : 'bg-gray-100 border-gray-200'
                    }`}>
                      <img src="https://flagcdn.com/w40/ae.png" alt="UAE" className="w-6 h-4" />
                      <span className={`font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>+971</span>
                    </div>
                    <div className="relative flex-1">
                      <Phone className={`absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      <Input
                        type="tel"
                        placeholder="50 123 4567"
                        value={formData.phone}
                        onChange={(e) => setFormData({ ...formData, phone: e.target.value.replace(/\D/g, '') })}
                        className={`pl-10 py-6 text-base ${
                          isDarkMode 
                            ? 'bg-[#1E293B] border-[#334155] text-white placeholder:text-gray-500' 
                            : 'bg-white border-gray-200 text-gray-900 placeholder:text-gray-400'
                        }`}
                        maxLength={9}
                        data-testid="phone-input"
                      />
                    </div>
                  </div>

                  <Button
                    onClick={sendPhoneCode}
                    disabled={loading || formData.phone.length < 8}
                    className="w-full bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] hover:opacity-90 text-white py-6 text-lg font-semibold"
                    data-testid="send-phone-code-btn"
                  >
                    {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('sendCode')}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 4: Verify Phone Code */}
            {step === 4 && (
              <div className="space-y-6" data-testid="step-4">
                <div className="text-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-[#38BDF8]/20' : 'bg-[#38BDF8]/10'}`}>
                    <Phone className="h-8 w-8 text-[#38BDF8]" />
                  </div>
                  <h1 className={`text-2xl sm:text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('verifyPhone')}</h1>
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{t('enterCode')}</p>
                  <p className="text-[#38BDF8] font-medium" dir="ltr">+971 {formData.phone}</p>
                </div>

                <OtpInputs value={formData.phoneCode} type="phone" />

                <Button
                  onClick={verifyPhoneCode}
                  disabled={loading || formData.phoneCode.length !== 6}
                  className="w-full bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] hover:opacity-90 text-white py-6 text-lg font-semibold"
                  data-testid="verify-phone-btn"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('verify')}
                </Button>

                <button
                  onClick={sendPhoneCode}
                  className="w-full text-[#38BDF8] hover:underline text-sm"
                  disabled={loading}
                >
                  {t('resendCode')}
                </button>
              </div>
            )}

            {/* Step 5: Enter Name (English Only) */}
            {step === 5 && (
              <div className="space-y-6" data-testid="step-5">
                <div className="text-center">
                  <div className={`w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 ${isDarkMode ? 'bg-green-500/20' : 'bg-green-100'}`}>
                    <CheckCircle className="h-8 w-8 text-green-500" />
                  </div>
                  <p className="text-green-500 font-medium mb-4">{t('phoneVerified')}</p>
                  <h1 className={`text-2xl sm:text-3xl font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('fullName')}</h1>
                  <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{t('fullNameNote')}</p>
                </div>

                <div className="space-y-4">
                  <div className="relative">
                    <User className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                    <Input
                      type="text"
                      placeholder={t('fullNamePlaceholder')}
                      value={formData.name}
                      onChange={handleNameChange}
                      className={`${language === 'ar' ? 'pr-10' : 'pl-10'} py-6 text-base ${nameError ? 'border-red-500' : ''} ${
                        isDarkMode 
                          ? 'bg-[#1E293B] border-[#334155] text-white placeholder:text-gray-500' 
                          : 'bg-white border-gray-200 text-gray-900'
                      }`}
                      dir="ltr"
                      data-testid="name-input"
                    />
                  </div>
                  {nameError && (
                    <p className="text-red-500 text-sm">{nameError}</p>
                  )}

                  <Button
                    onClick={saveName}
                    disabled={!formData.name.trim() || nameError}
                    className="w-full bg-gradient-to-r from-[#38BDF8] to-[#0EA5E9] hover:opacity-90 text-white py-6 text-lg font-semibold"
                    data-testid="save-name-btn"
                  >
                    {t('continue')}
                  </Button>
                </div>
              </div>
            )}

            {/* Step 6: Review & Create Account with Password */}
            {step === 6 && (
              <div className="space-y-5" data-testid="step-6">
                <div className="text-center">
                  <div className={`w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-3 ${isDarkMode ? 'bg-[#38BDF8]/20' : 'bg-[#38BDF8]/10'}`}>
                    <Lock className="h-7 w-7 text-[#38BDF8]" />
                  </div>
                  <h1 className={`text-xl sm:text-2xl font-bold mb-1 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {language === 'ar' ? 'أكمل إنشاء حسابك' : 'Complete Your Account'}
                  </h1>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                    {language === 'ar' ? 'أنشئ كلمة مرور قوية لحماية حسابك' : 'Create a strong password to secure your account'}
                  </p>
                </div>

                {/* User Info Summary */}
                <div className={`rounded-xl p-4 space-y-3 ${isDarkMode ? 'bg-[#1E293B]' : 'bg-gray-50'}`}>
                  <div className={`flex items-center justify-between p-2 rounded-lg ${isDarkMode ? 'bg-[#0F172A]' : 'bg-white'}`}>
                    <div className="flex items-center gap-2">
                      <User className="h-4 w-4 text-[#38BDF8]" />
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{formData.name}</span>
                    </div>
                  </div>

                  <div className={`flex items-center justify-between p-2 rounded-lg ${isDarkMode ? 'bg-[#0F172A]' : 'bg-white'}`}>
                    <div className="flex items-center gap-2">
                      <Phone className="h-4 w-4 text-[#38BDF8]" />
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`} dir="ltr">+971 {formData.phone}</span>
                    </div>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  </div>

                  <div className={`flex items-center justify-between p-2 rounded-lg ${isDarkMode ? 'bg-[#0F172A]' : 'bg-white'}`}>
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-[#38BDF8]" />
                      <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`} dir="ltr">{formData.email}</span>
                    </div>
                    <CheckCircle className="h-4 w-4 text-green-500" />
                  </div>
                </div>

                {/* Password Fields */}
                <div className="space-y-4">
                  <div>
                    <Label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {language === 'ar' ? 'كلمة المرور' : 'Password'}
                    </Label>
                    <div className="relative mt-1">
                      <Lock className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        placeholder={language === 'ar' ? 'أدخل كلمة المرور' : 'Enter password'}
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className={`${language === 'ar' ? 'pr-10 pl-10' : 'pl-10 pr-10'} py-5 ${
                          isDarkMode 
                            ? 'bg-[#1E293B] border-[#334155] text-white placeholder:text-gray-500' 
                            : 'bg-white border-gray-200 text-gray-900'
                        }`}
                        dir="ltr"
                        data-testid="password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={`absolute ${language === 'ar' ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600`}
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <div>
                    <Label className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {language === 'ar' ? 'تأكيد كلمة المرور' : 'Confirm Password'}
                    </Label>
                    <div className="relative mt-1">
                      <Lock className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 h-5 w-5 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
                      <Input
                        type={showConfirmPassword ? 'text' : 'password'}
                        placeholder={language === 'ar' ? 'أعد إدخال كلمة المرور' : 'Re-enter password'}
                        value={formData.confirmPassword}
                        onChange={(e) => setFormData({ ...formData, confirmPassword: e.target.value })}
                        className={`${language === 'ar' ? 'pr-10 pl-10' : 'pl-10 pr-10'} py-5 ${
                          isDarkMode 
                            ? 'bg-[#1E293B] border-[#334155] text-white placeholder:text-gray-500' 
                            : 'bg-white border-gray-200 text-gray-900'
                        }`}
                        dir="ltr"
                        data-testid="confirm-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        className={`absolute ${language === 'ar' ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600`}
                      >
                        {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                    {formData.confirmPassword && !doPasswordsMatch && (
                      <p className="text-red-500 text-xs mt-1">
                        {language === 'ar' ? 'كلمات المرور غير متطابقة' : 'Passwords do not match'}
                      </p>
                    )}
                  </div>

                  {/* Password Requirements */}
                  <div className={`rounded-lg p-3 ${isDarkMode ? 'bg-[#1E293B]' : 'bg-gray-50'}`}>
                    <p className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      {language === 'ar' ? 'متطلبات كلمة المرور:' : 'Password Requirements:'}
                    </p>
                    <div className="grid grid-cols-1 gap-1.5 text-xs">
                      <div className={`flex items-center gap-2 ${passwordRequirements.minLength ? 'text-green-500' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {passwordRequirements.minLength ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                        <span>{language === 'ar' ? '10 أحرف على الأقل' : 'At least 10 characters'}</span>
                      </div>
                      <div className={`flex items-center gap-2 ${passwordRequirements.hasUppercase ? 'text-green-500' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {passwordRequirements.hasUppercase ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                        <span>{language === 'ar' ? 'حرف كبير واحد (A-Z)' : 'One uppercase letter (A-Z)'}</span>
                      </div>
                      <div className={`flex items-center gap-2 ${passwordRequirements.hasLowercase ? 'text-green-500' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {passwordRequirements.hasLowercase ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                        <span>{language === 'ar' ? 'حرف صغير واحد (a-z)' : 'One lowercase letter (a-z)'}</span>
                      </div>
                      <div className={`flex items-center gap-2 ${passwordRequirements.hasNumber ? 'text-green-500' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {passwordRequirements.hasNumber ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                        <span>{language === 'ar' ? 'رقم واحد (0-9)' : 'One number (0-9)'}</span>
                      </div>
                      <div className={`flex items-center gap-2 ${passwordRequirements.hasSymbol ? 'text-green-500' : isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                        {passwordRequirements.hasSymbol ? <Check className="h-3.5 w-3.5" /> : <X className="h-3.5 w-3.5" />}
                        <span>{language === 'ar' ? 'رمز واحد (!@#$%...)' : 'One symbol (!@#$%...)'}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <Button
                  onClick={completeRegistration}
                  disabled={loading || !isPasswordValid || !doPasswordsMatch}
                  className="w-full bg-gradient-to-r from-[#1E9BD9] to-[#F97316] hover:opacity-90 text-white py-6 text-lg font-bold disabled:opacity-50"
                  data-testid="create-account-btn"
                >
                  {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : t('createAccount')}
                </Button>

                <p className={`text-center text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {t('termsNotice')}
                </p>
              </div>
            )}

            {/* Login Link */}
            <div className="mt-6 text-center">
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                {t('haveAccount')}{' '}
                <Link to="/login" className="text-[#38BDF8] hover:underline font-medium" data-testid="login-link">
                  {t('login')}
                </Link>
              </p>
            </div>
          </div>
        </div>

        {/* Step Indicator */}
        <div className="p-4 flex justify-center gap-2">
          {[1, 2, 3, 4, 5, 6].map((s) => (
            <div
              key={s}
              className={`w-2 h-2 rounded-full transition-all ${
                s === step ? 'w-6 bg-[#38BDF8]' : s < step ? 'bg-green-500' : isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
